import { z } from "zod";

export const portalAuthInputSchema = z.object({
	token: z.string().min(20).max(200),
});
export type PortalAuthInput = z.infer<typeof portalAuthInputSchema>;

export const portalBookingInputSchema = z.object({
	service_id: z.string().uuid().optional(),
	scheduled_at: z.string().datetime(),
	duration_minutes: z.number().int().positive().max(480).default(30),
	notes: z.string().max(1000).optional(),
});
export type PortalBookingInput = z.infer<typeof portalBookingInputSchema>;

export const portalAvailableSlotsQuerySchema = z.object({
	date: z.string().date(),
	duration_minutes: z.coerce.number().int().positive().max(480).default(30),
	service_id: z.string().uuid().optional(),
});
export type PortalAvailableSlotsQuery = z.infer<
	typeof portalAvailableSlotsQuerySchema
>;

export interface PortalServiceOption {
	id: string;
	name: { ar: string; en?: string };
	duration_minutes: number;
	price: string;
	currency: string;
	category: string | null;
	color: string | null;
}

/**
 * Minimum-necessary patient identity the portal exposes to the patient about
 * THEMSELVES. Deliberately excludes staff-internal fields the patient must not
 * see — clinical/admin `notes`, CRM `tags`, acquisition `source`, `is_active`,
 * and the `national_id` echo — which the full Patient record carries. Both the
 * login response and GET /portal/me return exactly this shape.
 */
export interface PortalPatientApi {
	id: string;
	full_name: string;
	mrn: string;
	phone: string;
	preferred_language: "ar" | "en";
}

export interface PortalAuthResponse {
	/** Same opaque token — patient keeps using it; we just confirmed it's valid. */
	token: string;
	/** ISO timestamp — once past this the portal redirects to a "link expired" page. */
	expires_at: string;
	patient: PortalPatientApi;
	clinic: {
		id: string;
		slug: string;
		name: { ar: string; en?: string };
		default_language: "ar" | "en";
	};
}

export interface GenerateAccessTokenResponse {
	token: string;
	expires_at: string;
	expires_in_seconds: number;
	portal_url: string;
	whatsapp_url: string;
	/** Pre-built bilingual message text for the staff to send. */
	message_text: { ar: string; en: string };
}

// ===== Portal: the patient's own results =====

/** A lab value the patient can see (their own results, simplified + safe). */
export const portalLabResultSchema = z.object({
	test_name: z.string(),
	value_numeric: z.number().nullable(),
	value_text: z.string().nullable(),
	unit: z.string().nullable(),
	abnormal_flag: z.string(),
	reference_low: z.number().nullable(),
	reference_high: z.number().nullable(),
	reference_text: z.string().nullable(),
	measured_at: z.string().datetime(),
});
export type PortalLabResult = z.infer<typeof portalLabResultSchema>;

/** A finalized radiology report the patient can see, with its share link. */
export const portalRadiologyReportSchema = z.object({
	id: z.string().uuid(),
	study_name: z.string(),
	modality: z.string(),
	impression: z.string().nullable(),
	finalized_at: z.string().datetime().nullable(),
	share_token: z.string(),
});
export type PortalRadiologyReport = z.infer<typeof portalRadiologyReportSchema>;
